import { useEffect, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Volume2 } from "lucide-react";
import { api } from "@/lib/api";
import { formatRands, shortId, statusLabel, VENDOR_NEXT } from "@/lib/order";
import { useRoom } from "@/hooks/use-room";
import { useOrderSound } from "@/hooks/use-order-sound";
import { StaffShell } from "@/components/layout/StaffShell";
import { vendorNav } from "@/lib/nav";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CenterSpinner, EmptyState } from "@/components/common/States";

const COLUMNS = [
  { key: "PENDING", title: "New", match: (s) => s === "PENDING" },
  { key: "VENDOR_ACCEPTED", title: "Accepted", match: (s) => s === "VENDOR_ACCEPTED" },
  { key: "PREPARING", title: "Preparing", match: (s) => s === "PREPARING" },
  { key: "READY_FOR_PICKUP", title: "Ready", match: (s) => s === "READY_FOR_PICKUP" },
  { key: "OUT", title: "Out for delivery", match: (s) => ["DRIVER_ASSIGNED", "PICKED_UP", "ON_THE_WAY"].includes(s) },
];

export default function VendorDashboard() {
  const qc = useQueryClient();
  const playSound = useOrderSound();
  const soundReady = useRef(false);

  const vendor = useQuery({ queryKey: ["my-vendor"], queryFn: () => api("/my/vendor") });
  const orders = useQuery({
    queryKey: ["vendor-orders"],
    queryFn: () => api("/orders?status=" + ["PENDING", "VENDOR_ACCEPTED", "PREPARING", "READY_FOR_PICKUP", "DRIVER_ASSIGNED", "PICKED_UP", "ON_THE_WAY"].join(",")),
    refetchInterval: 20_000,
  });

  useRoom(vendor.data ? `vendor:${vendor.data.vendor_id}` : null, (e) => {
    if (e.event === "ORDER_CREATED") {
      if (soundReady.current) playSound();
      toast.success("New order received!");
    }
    qc.invalidateQueries({ queryKey: ["vendor-orders"] });
  });

  useEffect(() => {
    const enable = () => {
      soundReady.current = true;
    };
    window.addEventListener("pointerdown", enable, { once: true });
    return () => window.removeEventListener("pointerdown", enable);
  }, []);

  const advance = async (order) => {
    const next = VENDOR_NEXT[order.status];
    if (!next) return;
    try {
      await api(`/orders/${order.order_id}/status`, { method: "PATCH", json: { status: next } });
      qc.invalidateQueries({ queryKey: ["vendor-orders"] });
    } catch (e) {
      toast.error(e.message);
    }
  };

  const reject = async (order) => {
    try {
      await api(`/orders/${order.order_id}/status`, { method: "PATCH", json: { status: "CANCELLED" } });
      qc.invalidateQueries({ queryKey: ["vendor-orders"] });
      toast.success("Order cancelled");
    } catch (e) {
      toast.error(e.message);
    }
  };

  let body;
  if (vendor.isLoading || orders.isLoading) {
    body = <CenterSpinner />;
  } else if (!vendor.data) {
    body = (
      <EmptyState
        title="No store yet"
        description="Set up your store details to start receiving orders."
        action={<Button className="rounded-full" onClick={() => (window.location.href = "/vendor/store")}>Set up store</Button>}
      />
    );
  } else {
    const all = orders.data || [];
    body = (
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-5">
        {COLUMNS.map((col) => {
          const items = all.filter((o) => col.match(o.status));
          return (
            <div key={col.key} className="flex flex-col rounded-2xl bg-muted/40 p-3">
              <div className="mb-3 flex items-center justify-between px-1">
                <h3 className="font-display text-sm">{col.title}</h3>
                <span className="rounded-full bg-background px-2 py-0.5 text-xs text-muted-foreground">{items.length}</span>
              </div>
              <div className="flex-1 space-y-3">
                {items.length === 0 ? (
                  <p className="px-1 py-6 text-center text-xs text-muted-foreground">Empty</p>
                ) : (
                  items.map((o) => (
                    <Card key={o.order_id} className="rounded-xl border p-3 shadow-sm" data-testid={`kanban-order-${o.order_id}`}>
                      <div className="flex items-center justify-between">
                        <span className="font-display text-sm">{shortId(o.order_id)}</span>
                        <span className="text-sm text-muted-foreground">{formatRands(o.total)}</span>
                      </div>
                      <p className="mt-0.5 text-xs text-muted-foreground">{o.customer_name}</p>
                      <ul className="mt-2 space-y-0.5 text-xs">
                        {o.items?.slice(0, 4).map((it) => (
                          <li key={it.product_id}>{it.quantity}× {it.name}</li>
                        ))}
                      </ul>
                      {VENDOR_NEXT[o.status] && (
                        <div className="mt-3 flex gap-2">
                          <Button size="sm" className="flex-1 rounded-full" onClick={() => advance(o)} data-testid={`advance-${o.order_id}`}>
                            {statusLabel(VENDOR_NEXT[o.status])}
                          </Button>
                          {o.status === "PENDING" && (
                            <Button size="sm" variant="outline" className="rounded-full text-destructive" onClick={() => reject(o)} data-testid={`reject-${o.order_id}`}>
                              Reject
                            </Button>
                          )}
                        </div>
                      )}
                      {col.key === "OUT" && <p className="mt-2 text-xs text-primary">{statusLabel(o.status)}{o.driver_name ? ` · ${o.driver_name}` : ""}</p>}
                    </Card>
                  ))
                )}
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <StaffShell
      items={vendorNav}
      roleLabel="Vendor"
      title="Live orders"
      headerRight={
        <Button variant="ghost" size="icon" onClick={() => { soundReady.current = true; playSound(); toast.info("Sound enabled"); }} data-testid="test-sound-button" title="Test alert sound">
          <Volume2 className="h-5 w-5" />
        </Button>
      }
    >
      {vendor.data?.status && vendor.data.status !== "approved" && (
        <Card className="mb-4 rounded-2xl border-[hsl(var(--warning))]/40 bg-[hsl(var(--warning))]/10 p-4 text-sm">
          Your store is <b>{vendor.data.status.replace("_", " ")}</b>. You won't receive orders until an admin approves and you go live.
        </Card>
      )}
      {body}
    </StaffShell>
  );
}
